/**
 * BrainService - Client for Alpha Dashboard (brain.py) via Upstash Redis
 *
 * Reads pre-calculated indicators, flow scores, and trading signals from Redis.
 * This avoids repeated API calls by using cached data from brain.py.
 *
 * Data available:
 * - brain:{symbol}:state - Real-time price, flow, toxicity, ATR
 * - brain:{symbol}:{tf} - RSI, MACD, Bollinger, etc. (15m, 1h, 4h)
 * - brain:kalman:{pair}:signal - Pair trading signals
 * - brain:market:overview - Market-wide metrics
 */

// XOR-obfuscated read-only token (safe to expose - read only access)
const OBFUSCATED_TOKEN = 'LAZ6JS0xIQYOKXwWAT4dFVQdWzAoGTAQGD5BDwc/Glg0OmwZWxwnVQAZXQUtHQ0OKABTNQgh';
const OBFUSCATION_KEY = 'mm4alpha';
const DEFAULT_UPSTASH_URL = 'https://central-tetra-17219.upstash.io';

// HIP-3 symbols with EMPTY orderbooks on Hyperliquid (no liquidity, can't trade)
// Updated: 2024-12-15
const EMPTY_ORDERBOOK_SYMBOLS = new Set([
    'xyz:COST', 'xyz:CRCL', 'xyz:GOLD', 'xyz:JPY', 'xyz:LLY',
    'xyz:MU', 'xyz:SKHX', 'xyz:SNDK', 'xyz:TSM'
]);

/**
 * Check if a symbol has orderbook liquidity
 */
function hasOrderbookLiquidity(symbol) {
    const normalized = symbol.includes(':') ? symbol : `xyz:${symbol}`;
    return !EMPTY_ORDERBOOK_SYMBOLS.has(normalized);
}

/**
 * Decode XOR-obfuscated token
 */
function decodeToken(encoded, key) {
    try {
        const decoded = atob(encoded);
        return decoded.split('').map((c, i) =>
            String.fromCharCode(c.charCodeAt(0) ^ key.charCodeAt(i % key.length))
        ).join('');
    } catch (e) {
        // console.error('[BRAIN-SERVICE] Token decode failed:', e);
        return null;
    }
}

export class BrainService {
    constructor() {
        this.upstashUrl = DEFAULT_UPSTASH_URL;
        this.upstashToken = decodeToken(OBFUSCATED_TOKEN, OBFUSCATION_KEY);
        this.timeout = 30000;

        // Cache with TTLs
        this.cache = new Map();
        this.stateTTL = 5 * 60 * 1000;        // 5 minutes for state
        this.indicatorTTL = 60 * 60 * 1000;   // 1 hour for indicators
        this.overviewTTL = 30 * 1000;         // 30 seconds for overview

        this.isConnected = false;

        // Auto-initialize
        this._init();
    }

    async _init() {
        try {
            await this.health();
            this.isConnected = true;
            // console.log('[BRAIN-SERVICE] Connected to Alpha Dashboard');
        } catch (e) {
            this.isConnected = false;
            // console.warn('[BRAIN-SERVICE] Not connected:', e.message);
        }
    }

    // =========================================================================
    // CACHE HELPERS
    // =========================================================================

    _getCached(key, ttl) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < ttl) {
            return cached.data;
        }
        return null;
    }

    _setCache(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }

    // =========================================================================
    // UPSTASH REST API
    // =========================================================================

    async _get(key) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(`${this.upstashUrl}/get/${key}`, {
                signal: controller.signal,
                headers: { 'Authorization': `Bearer ${this.upstashToken}` }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                const result = data.result;
                if (result) {
                    try {
                        return JSON.parse(result);
                    } catch {
                        return result;
                    }
                }
            }
            return null;
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    async _keys(pattern) {
        // Note: KEYS command may not be allowed on read-only tokens
        // Use SCAN instead which is allowed
        return this._scan(pattern);
    }

    async _scan(pattern, count = 100) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            // Use SCAN with MATCH pattern - read-only tokens allow this
            const response = await fetch(`${this.upstashUrl}/scan/0/match/${encodeURIComponent(pattern)}/count/${count}`, {
                signal: controller.signal,
                headers: { 'Authorization': `Bearer ${this.upstashToken}` }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                // SCAN returns [cursor, [keys...]]
                if (data.result && Array.isArray(data.result) && data.result.length >= 2) {
                    return data.result[1] || [];
                }
                return [];
            }
            return [];
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    async _zrevrange(key, start, stop) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(`${this.upstashUrl}/zrevrange/${key}/${start}/${stop}`, {
                signal: controller.signal,
                headers: { 'Authorization': `Bearer ${this.upstashToken}` }
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                return data.result || [];
            }
            return [];
        } catch (error) {
            clearTimeout(timeoutId);
            throw error;
        }
    }

    // =========================================================================
    // HEALTH CHECK
    // =========================================================================

    async health() {
        try {
            // Try to get a known test key or market overview
            // Note: Read-only token can't use KEYS command, so we test with GET
            const testResult = await this._get('brain:market:overview');
            // Even if null, connection worked if no error thrown
            this.isConnected = true;
            return { status: 'ok', upstash_url: this.upstashUrl, hasData: !!testResult };
        } catch (error) {
            this.isConnected = false;
            throw new Error(`Upstash connection failed: ${error.message}`);
        }
    }

    // =========================================================================
    // SYMBOLS
    // =========================================================================

    async getSymbols() {
        const cacheKey = 'symbols';
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        // Look for spread keys (brain:xyz:SYMBOL:spread) - primary data source
        // Pattern needs two wildcards: brain:*:*:spread matches brain:xyz:NVDA:spread
        const keys = await this._keys('brain:*:*:spread');
        const symbols = [];
        for (const key of keys) {
            const parts = key.split(':');
            // Key format: brain:xyz:SYMBOL:spread (4 parts)
            // Symbol is parts[1]:parts[2], e.g., "xyz:NVDA"
            if (parts.length >= 4 && parts[3] === 'spread') {
                const symbol = `${parts[1]}:${parts[2]}`;
                // Filter out symbols with empty orderbooks
                if (hasOrderbookLiquidity(symbol)) {
                    symbols.push(symbol);
                }
            }
        }
        const result = [...new Set(symbols)].sort();
        this._setCache(cacheKey, result);
        return result;
    }

    // =========================================================================
    // STATE
    // =========================================================================

    async getState(symbol) {
        const cacheKey = `state:${symbol}`;
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        const state = await this._get(`brain:${symbol}:state`);
        if (!state) {
            throw new Error(`Symbol not found: ${symbol}`);
        }

        this._setCache(cacheKey, state);
        return state;
    }

    async getAllStates() {
        const cacheKey = 'all_states';
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        const symbols = await this.getSymbols();
        const states = {};

        for (const symbol of symbols) {
            try {
                const state = await this._get(`brain:${symbol}:state`);
                if (state) {
                    states[symbol] = state;
                }
            } catch {
                // Skip failed symbols
            }
        }

        this._setCache(cacheKey, states);
        return states;
    }

    // =========================================================================
    // CONVENIENCE METHODS (for ATRService integration)
    // =========================================================================

    async getATR(symbol) {
        try {
            const state = await this.getState(symbol);
            if (state && state.volatility) {
                return {
                    atr: state.volatility.atr || 0,
                    atrPct: state.volatility.atr_pct || 0
                };
            }
            return null;
        } catch {
            return null;
        }
    }

    async getFlowScore(symbol) {
        try {
            const state = await this.getState(symbol);
            return state?.flow?.score || 0;
        } catch {
            return 0;
        }
    }

    async getToxicity(symbol) {
        try {
            const state = await this.getState(symbol);
            return state?.flow?.toxicity || 0;
        } catch {
            return 0;
        }
    }

    async isToxic(symbol, threshold = 0.7) {
        const toxicity = await this.getToxicity(symbol);
        return toxicity > threshold;
    }

    // =========================================================================
    // INDICATORS
    // =========================================================================

    async getIndicators(symbol, timeframe = null) {
        if (timeframe) {
            const cacheKey = `indicators:${symbol}:${timeframe}`;
            const cached = this._getCached(cacheKey, this.indicatorTTL);
            if (cached) return cached;

            const indicators = await this._get(`brain:${symbol}:${timeframe}`);
            if (!indicators) {
                throw new Error(`Indicators not found: ${symbol} ${timeframe}`);
            }

            this._setCache(cacheKey, indicators);
            return indicators;
        }

        // Get all timeframes
        const result = {};
        for (const tf of ['15m', '1h', '4h']) {
            const cacheKey = `indicators:${symbol}:${tf}`;
            let indicators = this._getCached(cacheKey, this.indicatorTTL);

            if (!indicators) {
                indicators = await this._get(`brain:${symbol}:${tf}`);
                if (indicators) {
                    this._setCache(cacheKey, indicators);
                }
            }

            if (indicators) {
                result[tf] = indicators;
            }
        }

        if (Object.keys(result).length === 0) {
            throw new Error(`No indicators found: ${symbol}`);
        }

        return { symbol, indicators: result };
    }

    // =========================================================================
    // ALERTS
    // =========================================================================

    async getAlerts(symbol = null, limit = 50) {
        let alertsRaw;

        if (symbol) {
            alertsRaw = await this._zrevrange(`brain:alert_history:${symbol}`, 0, limit - 1);
        } else {
            const keys = await this._keys('brain:alert_history:*');
            alertsRaw = [];
            for (const key of keys.slice(0, 20)) {
                const symbolAlerts = await this._zrevrange(key, 0, 10);
                alertsRaw.push(...symbolAlerts);
            }
        }

        // Parse alerts
        const alerts = [];
        for (const alertStr of alertsRaw) {
            try {
                const alert = typeof alertStr === 'string' ? JSON.parse(alertStr) : alertStr;
                alerts.push(alert);
            } catch {
                // Skip malformed alerts
            }
        }

        // Sort by timestamp and limit
        alerts.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        return alerts.slice(0, limit);
    }

    // =========================================================================
    // KALMAN PAIRS
    // =========================================================================

    async getKalmanPairs() {
        const cacheKey = 'kalman_pairs';
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        // Get all kalman signal keys (brain:kalman:{pair}:signal)
        const keys = await this._keys('brain:kalman:*:signal');
        const pairs = [];

        for (const key of keys) {
            const parts = key.split(':');
            if (parts.length >= 4) {
                const pairId = parts[2];
                const signal = await this._get(key);
                if (signal) {
                    const symbolA = signal.symbol_a || pairId.split('_')[0];
                    const symbolB = signal.symbol_b || pairId.split('_')[1];

                    // Filter out pairs with illiquid symbols (empty orderbooks)
                    if (!hasOrderbookLiquidity(symbolA) || !hasOrderbookLiquidity(symbolB)) {
                        continue;
                    }

                    pairs.push({
                        pair_id: pairId,
                        symbol_a: symbolA,
                        symbol_b: symbolB,
                        zscore: signal.zscore || 0,
                        action: signal.action || 'hold',
                        hedge_ratio: signal.hedge_ratio || 1.0,
                        half_life: signal.half_life || 0,
                        correlation: signal.correlation || 0,
                        is_cointegrated: signal.cointegrated || false,
                        observations: signal.observations || 0,
                        // Multi-timeframe data
                        timeframes: signal.timeframes || {},
                        cointegrated_timeframes: signal.cointegrated_timeframes || []
                    });
                }
            }
        }

        // Sort by absolute zscore (most actionable first)
        pairs.sort((a, b) => Math.abs(b.zscore) - Math.abs(a.zscore));

        this._setCache(cacheKey, pairs);
        return pairs;
    }

    async getKalmanSignal(pair) {
        const cacheKey = `kalman_signal:${pair}`;
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        const signal = await this._get(`brain:kalman:${pair}:signal`);
        if (!signal) {
            throw new Error(`Pair not found: ${pair}`);
        }

        this._setCache(cacheKey, signal);
        return signal;
    }

    // =========================================================================
    // MARKET OVERVIEW
    // =========================================================================

    async getMarketOverview() {
        const cacheKey = 'market_overview';
        const cached = this._getCached(cacheKey, this.overviewTTL);
        if (cached) return cached;

        // Build overview from spread signals (primary data source)
        const spreads = await this.getSpreadSignals();
        const symbolsData = [];
        let totalZscore = 0;

        for (const spread of spreads) {
            symbolsData.push({
                symbol: spread.symbol,
                zscore: Math.abs(spread.zscore || 0),
                spread_bps: spread.spread_bps || 0,
                direction: spread.direction || 'neutral'
            });
            totalZscore += Math.abs(spread.zscore || 0);
        }

        const count = spreads.length;
        const avgZscore = count > 0 ? totalZscore / count : 0;

        // Risk based on average z-score (>2 = high, >1 = medium)
        const marketRisk = avgZscore > 2 ? 'high' : avgZscore > 1 ? 'medium' : 'low';

        const overview = {
            timestamp: Date.now(),
            symbols_tracked: count,
            avg_toxicity: avgZscore / 3,  // Normalize z-score to 0-1 range for display
            market_risk: marketRisk,
            top_toxic: symbolsData.sort((a, b) => b.zscore - a.zscore).slice(0, 5).map(s => ({
                symbol: s.symbol,
                toxicity: s.zscore / 3,
                flow_score: s.spread_bps
            }))
        };

        this._setCache(cacheKey, overview);
        return overview;
    }

    // =========================================================================
    // SPREAD SIGNALS (Predator Architecture)
    // =========================================================================

    async getSpreadSignals() {
        const cacheKey = 'spread_signals';
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        // Get all spread keys (brain:xyz:SYMBOL:spread)
        // Pattern needs two wildcards: brain:*:*:spread matches brain:xyz:NVDA:spread
        const keys = await this._keys('brain:*:*:spread');
        const spreads = [];

        for (const key of keys) {
            const parts = key.split(':');
            // Key format: brain:xyz:SYMBOL:spread (4 parts)
            if (parts.length >= 4 && parts[3] === 'spread') {
                const symbol = `${parts[1]}:${parts[2]}`;

                // Filter out symbols with empty orderbooks
                if (!hasOrderbookLiquidity(symbol)) {
                    continue;
                }

                const spread = await this._get(key);
                if (spread) {
                    spreads.push({
                        symbol,
                        timestamp: spread.timestamp,
                        hl_mid: spread.price?.hl_mid || 0,
                        stock_mid: spread.price?.stock_mid || 0,
                        spread_bps: spread.spread?.bps || 0,
                        zscore: spread.spread?.zscore || 0,
                        threshold: spread.spread?.threshold || 2.0,
                        half_life_sec: spread.spread?.half_life_sec || 0,
                        direction: spread.signal?.direction || 'neutral',
                        attack_side: spread.signal?.attack_side || 'neutral',
                        confidence: spread.signal?.confidence || 0,
                        is_valid: spread.signal?.is_valid || false,
                        // Predator MM alignment
                        mm_alignment_score: spread.mm_alignment?.score || 0,
                        vulnerable_mm_exposure: spread.mm_alignment?.vulnerable_exposure || 0,
                        // Predator cluster stats
                        tracked_wallets: spread.cluster?.tracked_wallets || 0,
                        long_exposure: spread.cluster?.long_exposure || 0,
                        short_exposure: spread.cluster?.short_exposure || 0,
                        net_exposure: spread.cluster?.net_exposure || 0,
                        avg_vulnerability: spread.cluster?.avg_vulnerability || 0,
                        // Exit zones and offside wallets (count only - full data via getSpread)
                        exit_zones_count: spread.exit_zones?.length || 0,
                        offside_wallets_count: spread.offside_wallets?.length || 0,
                        wallets_count: spread.wallets?.count || 0
                    });
                }
            }
        }

        // Sort by absolute zscore (most actionable first)
        spreads.sort((a, b) => Math.abs(b.zscore) - Math.abs(a.zscore));

        this._setCache(cacheKey, spreads);
        return spreads;
    }

    async getSpread(symbol) {
        const cacheKey = `spread:${symbol}`;
        const cached = this._getCached(cacheKey, this.stateTTL);
        if (cached) return cached;

        const spread = await this._get(`brain:${symbol}:spread`);
        if (!spread) {
            return null;
        }

        this._setCache(cacheKey, spread);
        return spread;
    }

    // =========================================================================
    // WATCH WALLETS
    // =========================================================================

    async getWatchWallets() {
        // Get wallet info from any spread signal (they all have the same wallet list)
        const spreads = await this.getSpreadSignals();
        if (spreads.length > 0) {
            const firstSpread = await this.getSpread(spreads[0].symbol);
            if (firstSpread?.wallets) {
                return {
                    count: firstSpread.wallets.count || 0,
                    top_wallets: firstSpread.wallets.top_5 || []
                };
            }
        }
        return { count: 0, top_wallets: [] };
    }

    // =========================================================================
    // LIQUIDATION HEATMAP
    // =========================================================================

    /**
     * Get global liquidation heatmap across all HIP-3 positions
     * Shows price levels where liquidations will occur
     */
    async getLiquidationHeatmap() {
        const cacheKey = 'liquidation_heatmap';
        const cached = this._getCached(cacheKey, 30000); // 30 sec cache
        if (cached) return cached;

        const heatmap = await this._get('brain:liquidation_heatmap');
        if (!heatmap) {
            return {
                timestamp: 0,
                total_positions: 0,
                total_volume_at_risk: 0,
                levels: [],
                by_symbol: {}
            };
        }

        this._setCache(cacheKey, heatmap);
        return heatmap;
    }

    /**
     * Get liquidation data for a specific symbol
     */
    async getSymbolLiquidation(symbol) {
        const cacheKey = `liquidation:${symbol}`;
        const cached = this._getCached(cacheKey, 30000);
        if (cached) return cached;

        const data = await this._get(`brain:${symbol}:liquidation`);
        if (!data) {
            return {
                long_liq_volume: 0,
                short_liq_volume: 0,
                levels: []
            };
        }

        this._setCache(cacheKey, data);
        return data;
    }

    // =========================================================================
    // PREDATOR: EXIT ZONES
    // =========================================================================

    async getExitZones(symbol) {
        const spread = await this.getSpread(symbol);
        if (!spread?.exit_zones) {
            return [];
        }
        return spread.exit_zones.map(zone => ({
            price: zone.price || 0,
            volume_at_risk: zone.volume_at_risk || 0,
            wallet_count: zone.wallet_count || 0,
            cascade_probability: zone.cascade_probability || 0,
            type: zone.type || 'behavioral'
        }));
    }

    async getAllExitZones() {
        const spreads = await this.getSpreadSignals();
        const allZones = {};

        for (const s of spreads) {
            if (s.exit_zones_count > 0) {
                const zones = await this.getExitZones(s.symbol);
                if (zones.length > 0) {
                    allZones[s.symbol] = zones;
                }
            }
        }

        return allZones;
    }

    // =========================================================================
    // PREDATOR: OFFSIDE WALLETS
    // =========================================================================

    async getOffsideWallets(symbol) {
        const spread = await this.getSpread(symbol);
        if (!spread?.offside_wallets) {
            return [];
        }
        return spread.offside_wallets.map(w => ({
            address: w.address || '',
            vulnerability_score: w.vulnerability_score || 0
        }));
    }

    async getAllOffsideWallets() {
        const spreads = await this.getSpreadSignals();
        const allOffside = {};

        for (const s of spreads) {
            if (s.offside_wallets_count > 0) {
                const wallets = await this.getOffsideWallets(s.symbol);
                if (wallets.length > 0) {
                    allOffside[s.symbol] = wallets;
                }
            }
        }

        return allOffside;
    }

    // =========================================================================
    // PREDATOR: CLUSTER STATS
    // =========================================================================

    async getClusterStats(symbol) {
        const spread = await this.getSpread(symbol);
        if (!spread?.cluster) {
            return null;
        }
        return {
            tracked_wallets: spread.cluster.tracked_wallets || 0,
            long_exposure: spread.cluster.long_exposure || 0,
            short_exposure: spread.cluster.short_exposure || 0,
            net_exposure: spread.cluster.net_exposure || 0,
            avg_vulnerability: spread.cluster.avg_vulnerability || 0
        };
    }

    // =========================================================================
    // CLEAR CACHE
    // =========================================================================

    clearCache() {
        this.cache.clear();
        // console.log('[BRAIN-SERVICE] Cache cleared');
    }
}

// Create singleton instance
const brainService = new BrainService();
window.BrainService = brainService;

export { brainService };
